import { useState } from 'react';

import { ControllerRenderProps, UseFormReturn } from 'react-hook-form';

import { BasicInfoForm } from './schema';
import { FallbackDateFormat, MatterLanguage } from '../../../../../../../backend/src/types/types';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { Icon } from '@/new/components/icon';
import { NewChip } from '@/new/components/new-chip';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/new/components/shadcn/form';
import { Input } from '@/new/components/shadcn/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { TooltipIcon } from '@/new/components/tooltip-icon';

export const MATTER_LANGUAGE_LABELS: Record<MatterLanguage, string> = {
  [MatterLanguage.EnglishUK]: 'English (UK)',
  [MatterLanguage.EnglishUS]: 'English (US)',
  [MatterLanguage.French]: 'French',
  [MatterLanguage.German]: 'German',
  [MatterLanguage.Dutch]: 'Dutch',
  [MatterLanguage.Italian]: 'Italian',
};

const MATTER_LANGUAGE_TOOLTIP =
  "This is the language this Matter's documents will be processed in. Facts, Inconsistencies, and Dramatis will be generated in this language.";

// The frontend can't see the deployment's date convention, so Auto is not resolved to a concrete format here.
export const FALLBACK_DATE_FORMAT_LABELS: Record<FallbackDateFormat, string> = {
  [FallbackDateFormat.Auto]: 'Auto (regional default)',
  [FallbackDateFormat.DayFirst]: 'DD/MM/YYYY',
  [FallbackDateFormat.MonthFirst]: 'MM/DD/YYYY',
};

export const FALLBACK_DATE_FORMAT_DESCRIPTION =
  'Wexler infers the date order from context in each document first, and only uses this when a document has no indicators either way. This cannot be changed once the matter is created.';

interface MatterLanguageSelectProps {
  field: ControllerRenderProps<BasicInfoForm, 'language'>;
}

function MatterLanguageSelect({ field }: MatterLanguageSelectProps) {
  const currentLanguage = field.value ?? MatterLanguage.EnglishUK;
  const [pendingLanguage, setPendingLanguage] = useState<MatterLanguage | null>(null);

  const handleValueChange = (value: string) => {
    const nextLanguage = value as MatterLanguage;
    if (nextLanguage === currentLanguage) return;
    // Confirm before applying: the language drives how every downstream output is generated.
    setPendingLanguage(nextLanguage);
  };

  const confirmChange = () => {
    if (pendingLanguage) {
      field.onChange(pendingLanguage);
    }
    setPendingLanguage(null);
  };

  return (
    <>
      <Select onValueChange={handleValueChange} value={currentLanguage}>
        <FormControl>
          <SelectTrigger data-testid="matter-creator-language-field">
            <SelectValue placeholder="Select a language" />
          </SelectTrigger>
        </FormControl>
        <SelectContent>
          {Object.values(MatterLanguage).map((language) => (
            <SelectItem key={language} value={language}>
              {MATTER_LANGUAGE_LABELS[language]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <ConfirmationModal
        isOpen={pendingLanguage !== null}
        onOpenChange={(open) => {
          if (!open) setPendingLanguage(null);
        }}
        title="Change Matter Language?"
        confirmLabel="Confirm"
        onConfirm={confirmChange}
        isLoading={false}
      >
        <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <Icon className="lucide--triangle-alert mt-0.5 shrink-0 text-amber-600" />
          <p>This cannot be changed after Matter creation.</p>
        </div>
        <p className="text-sm text-gray-700">
          This Matter's documents will be processed in{' '}
          <span className="font-medium">{pendingLanguage ? MATTER_LANGUAGE_LABELS[pendingLanguage] : ''}</span>. Facts,
          Inconsistencies, and Dramatis will be generated in this language.{' '}
        </p>
      </ConfirmationModal>
    </>
  );
}

interface BasicInfoStepProps {
  form: UseFormReturn<BasicInfoForm>;
  requireCode: boolean;
}

export function MatterCreatorBasic({ form, requireCode }: BasicInfoStepProps) {
  return (
    <div className="mx-auto w-full max-w-3xl px-3 py-6">
      <div className="my-8">
        <h2 className="font-serif text-xl font-medium">Basic Matter Information</h2>
      </div>

      <div className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormMessage />
              <FormLabel required>Matter Name</FormLabel>
              <p className="-mt-0.5 mb-3 text-xs whitespace-normal text-gray-500">This can be updated later.</p>
              <FormControl>
                <Input {...field} placeholder="Company A vs Company B" data-testid="matter-creator-name-field" />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="language"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                <span>Matter Language</span>
                <TooltipIcon side="top" tooltipContent={MATTER_LANGUAGE_TOOLTIP} triggerClassName="h-3.5 w-3.5" />
                <NewChip className="relative top-auto right-auto ml-1 py-0.5" />
              </FormLabel>
              <p className="-mt-0.5 mb-3 text-xs whitespace-normal text-gray-500">
                The primary language of this Matter. This cannot be changed once the matter is created.
              </p>
              <MatterLanguageSelect field={field} />
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="fallbackDateFormat"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Fallback Date Format</FormLabel>
              <p className="-mt-0.5 mb-3 text-xs whitespace-normal text-gray-500">{FALLBACK_DATE_FORMAT_DESCRIPTION}</p>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger data-testid="matter-creator-fallback-date-format-field">
                    <SelectValue placeholder="Select a date format" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {Object.values(FallbackDateFormat).map((format) => (
                    <SelectItem key={format} value={format}>
                      {FALLBACK_DATE_FORMAT_LABELS[format]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <hr className="my-10" />

        <FormField
          control={form.control}
          name="code"
          render={({ field }) => (
            <FormItem>
              {requireCode ? (
                <FormLabel required>Matter Code</FormLabel>
              ) : (
                <FormLabel>
                  Matter Code <span className="font-normal text-gray-400">Optional</span>
                </FormLabel>
              )}
              <FormControl>
                <Input {...field} placeholder="WEX-123" data-testid="matter-creator-code-field" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="tokenLimit"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Matter Page Limit <span className="font-normal text-gray-400">Optional</span>
                <p className="mt-2 text-xs whitespace-normal text-gray-500">
                  The maximum number of pages that can be processed in this Matter before always requiring approval.
                </p>
              </FormLabel>

              <FormControl>
                <Input
                  {...field}
                  type="number"
                  data-testid="matter-creator-token-limit-field"
                  min="0"
                  step="1000"
                  onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                  value={field.value ?? ''}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
